import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Plus, Copy, Trash2, ChevronDown, Check } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { useResizable } from "../../hooks/useResizable";
import { ResizeHandles } from "../ui/resize-handles";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";

// --- Types ---

export type FilterValue =
  | { type: "text"; value: string }
  | { type: "enum"; value: string }
  | { type: "fk"; value: string }
  | { type: "date"; from: string; to: string }
  | { type: "boolean"; value: "yes" | "no" }
  | { type: "number"; min: string; max: string };

export interface ListView {
  id: string;
  name: string;
  columns: string[];
  filters: Record<string, FilterValue>;
  isDefault: boolean;
}

// --- Storage ---

const STORAGE_KEY = "bizcoach-admin-views";
const ACTIVE_VIEW_KEY = "bizcoach-admin-active-view";

function loadFromStorage(): Record<string, ListView[]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveToStorage(views: Record<string, ListView[]>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(views));
}

function loadActiveViewIds(): Record<string, string> {
  try {
    const raw = localStorage.getItem(ACTIVE_VIEW_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveActiveViewIds(ids: Record<string, string>) {
  localStorage.setItem(ACTIVE_VIEW_KEY, JSON.stringify(ids));
}

// --- Default views ---

const DEFAULT_HIDDEN: Record<string, Set<string>> = {
  users: new Set(["id", "profilePicture", "zohoCrmContactId", "linkedinUrl"]),
  client_accounts: new Set(["id", "description", "tva", "zohoCrmAccountId", "zohoBillingAccountId"]),
  simulators: new Set(["id", "systemPrompt"]),
  scenario_requests: new Set(["id"]),
  // ⚠️ Duplicat volontaire de DEFAULT_HIDDEN dans AdminPage.tsx — garder les deux alignés.
  scenarios: new Set(["id", "userMessage", "raw", "variables", "priorContext", "prospectCompanyDescription"]),
  audio_sessions: new Set(["id"]),

  targets: new Set(["id", "decisionMakerProfile", "painPoints"]),
  sessions: new Set(["id"]),
  scenario_chat_messages: new Set(["id", "content"]),
  n8n_workflows: new Set(["id"]),
  feedbacks: new Set(["id", "content", "raw"]),
};

const GLOBAL_HIDDEN = new Set(["id", "systemPrompt"]);

function getVisibleColumns(tableName: string, allColumns: string[]): string[] {
  const hidden = DEFAULT_HIDDEN[tableName] || GLOBAL_HIDDEN;
  return allColumns.filter((c) => !hidden.has(c));
}

function buildDefaultViews(tableName: string, allColumns: string[]): ListView[] {
  const visibleCols = getVisibleColumns(tableName, allColumns);

  switch (tableName) {
    case "users":
      return [
        {
          id: "users-tous",
          name: "Tous",
          columns: visibleCols,
          filters: {},
          isDefault: true,
        },
        {
          id: "users-actifs",
          name: "Actifs",
          columns: visibleCols,
          filters: { status: { type: "enum", value: "active" } },
          isDefault: true,
        },
        {
          id: "users-invites",
          name: "Invités",
          columns: visibleCols,
          filters: { status: { type: "enum", value: "invited" } },
          isDefault: true,
        },
      ];
    case "client_accounts":
      return [
        {
          id: "ca-tous",
          name: "Tous",
          columns: visibleCols,
          filters: {},
          isDefault: true,
        },
      ];
    default:
      return [
        {
          id: `${tableName}-tous`,
          name: "Tous",
          columns: visibleCols,
          filters: {},
          isDefault: true,
        },
      ];
  }
}

// --- Hook ---

export function useListViews(tableName: string, allColumns: string[]) {
  const [allViews, setAllViews] = useState<Record<string, ListView[]>>(() => loadFromStorage());
  const [activeViewIds, setActiveViewIds] = useState<Record<string, string>>(() => loadActiveViewIds());

  // Merge default + stored views for current table, pruning invalid columns
  const views: ListView[] = React.useMemo(() => {
    const defaults = buildDefaultViews(tableName, allColumns);
    const stored = allViews[tableName] || [];
    const validCols = new Set(allColumns);

    // Merge: keep defaults (updating their columns if schema changed), add custom
    const defaultIds = new Set(defaults.map((d) => d.id));
    const merged: ListView[] = defaults.map((def) => {
      // Check if user has overridden default view columns/filters
      const override = stored.find((s) => s.id === def.id);
      if (override) {
        return {
          ...override,
          isDefault: true,
          columns: override.columns.filter((c) => validCols.has(c)),
        };
      }
      return def;
    });

    // Add custom (non-default) views
    for (const s of stored) {
      if (!defaultIds.has(s.id)) {
        merged.push({
          ...s,
          columns: s.columns.filter((c) => validCols.has(c)),
        });
      }
    }

    return merged;
  }, [tableName, allColumns.join(","), allViews]);

  const fallbackView: ListView = {
    id: `${tableName}-fallback`,
    name: "Tous",
    columns: allColumns,
    filters: {},
    isDefault: true,
  };
  const activeViewId = activeViewIds[tableName] || views[0]?.id;
  const activeView = views.find((v) => v.id === activeViewId) || views[0] || fallbackView;

  // Persist views
  const persistViews = useCallback(
    (updated: ListView[]) => {
      setAllViews((prev) => {
        const next = { ...prev, [tableName]: updated };
        saveToStorage(next);
        return next;
      });
    },
    [tableName]
  );

  // Persist active view
  const persistActiveView = useCallback(
    (viewId: string) => {
      setActiveViewIds((prev) => {
        const next = { ...prev, [tableName]: viewId };
        saveActiveViewIds(next);
        return next;
      });
    },
    [tableName]
  );

  const switchView = useCallback(
    (viewId: string) => {
      persistActiveView(viewId);
    },
    [persistActiveView]
  );

  const createView = useCallback(
    (name: string, columns: string[], filters: Record<string, FilterValue>) => {
      const newView: ListView = {
        id: crypto.randomUUID(),
        name,
        columns,
        filters,
        isDefault: false,
      };
      const updated = [...views, newView];
      persistViews(updated);
      persistActiveView(newView.id);
      return newView;
    },
    [views, persistViews, persistActiveView]
  );

  const updateView = useCallback(
    (viewId: string, updates: Partial<Pick<ListView, "name" | "columns" | "filters">>) => {
      const updated = views.map((v) =>
        v.id === viewId ? { ...v, ...updates } : v
      );
      persistViews(updated);
    },
    [views, persistViews]
  );

  const duplicateView = useCallback(
    (viewId: string) => {
      const source = views.find((v) => v.id === viewId);
      if (!source) return;
      const newView: ListView = {
        id: crypto.randomUUID(),
        name: `${source.name} (copie)`,
        columns: [...source.columns],
        filters: { ...source.filters },
        isDefault: false,
      };
      const updated = [...views, newView];
      persistViews(updated);
      persistActiveView(newView.id);
    },
    [views, persistViews, persistActiveView]
  );

  const deleteView = useCallback(
    (viewId: string) => {
      const view = views.find((v) => v.id === viewId);
      if (!view || view.isDefault) return;
      const updated = views.filter((v) => v.id !== viewId);
      persistViews(updated);
      if (activeViewId === viewId) {
        persistActiveView(updated[0]?.id || "");
      }
    },
    [views, activeViewId, persistViews, persistActiveView]
  );

  return {
    views,
    activeView,
    switchView,
    createView,
    updateView,
    duplicateView,
    deleteView,
  };
}

// --- SortableColumnItem ---

function SortableColumnItem({
  col,
  checked,
  onToggle,
}: {
  col: string;
  checked: boolean;
  onToggle: (col: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: col });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-md border text-sm ${
        checked ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100 text-slate-400"
      }`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing touch-none text-slate-400 hover:text-slate-600"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <label className="flex items-center gap-2 flex-1 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(col)}
          className="rounded border-slate-300 text-[#384872] focus:ring-[#384872]/30"
        />
        <span className={checked ? "text-slate-700" : "text-slate-400"}>{col}</span>
      </label>
    </div>
  );
}

// --- ViewEditorModal ---

export function ViewEditorModal({
  view,
  allColumns,
  onSave,
  onClose,
  renderFilterControl,
  lookupMaps,
}: {
  view: ListView;
  allColumns: string[];
  onSave: (updates: { name: string; columns: string[]; filters: Record<string, FilterValue> }) => void;
  onClose: () => void;
  renderFilterControl: (
    col: string,
    currentFilter: FilterValue | undefined,
    setFilter: (col: string, filter: FilterValue | undefined) => void,
    lookupMaps?: Record<string, Map<string, string>>
  ) => React.ReactNode;
  lookupMaps?: Record<string, Map<string, string>>;
}) {
  const [name, setName] = useState(view.name);
  const [orderedColumns, setOrderedColumns] = useState<string[]>(() => {
    // Start with view columns (checked, in order), then unchecked columns
    const checked = new Set(view.columns);
    const unchecked = allColumns.filter((c) => !checked.has(c));
    return [...view.columns, ...unchecked];
  });
  const [checkedColumns, setCheckedColumns] = useState<Set<string>>(
    () => new Set(view.columns)
  );
  const [filters, setFilters] = useState<Record<string, FilterValue>>({ ...view.filters });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrderedColumns((prev) => {
        const oldIndex = prev.indexOf(String(active.id));
        const newIndex = prev.indexOf(String(over.id));
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const toggleColumn = (col: string) => {
    setCheckedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(col)) {
        next.delete(col);
      } else {
        next.add(col);
      }
      return next;
    });
  };

  const setFilter = (col: string, filter: FilterValue | undefined) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (filter) {
        next[col] = filter;
      } else {
        delete next[col];
      }
      return next;
    });
  };

  const handleSave = () => {
    const finalColumns = orderedColumns.filter((c) => checkedColumns.has(c));
    onSave({ name: name.trim() || view.name, columns: finalColumns, filters });
  };

  // Split ordered columns: checked first, then unchecked
  const checkedOrdered = orderedColumns.filter((c) => checkedColumns.has(c));
  const uncheckedOrdered = orderedColumns.filter((c) => !checkedColumns.has(c));
  const displayOrder = [...checkedOrdered, ...uncheckedOrdered];

  const { size: viewEditorSize, containerRef: viewEditorRef, onPointerDown: viewEditorPointerDown } = useResizable({
    minWidth: 400,
    minHeight: 350,
    maxWidth: typeof window !== "undefined" ? window.innerWidth - 32 : 1200,
    maxHeight: typeof window !== "undefined" ? window.innerHeight - 32 : 900,
  });

  const mergedRef = useCallback((node: HTMLDivElement | null) => {
    (viewEditorRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, [viewEditorRef]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        ref={mergedRef}
        className="flex flex-col overflow-hidden"
        style={{
          width: viewEditorSize.width ? `${viewEditorSize.width}px` : undefined,
          maxWidth: viewEditorSize.width ? "none" : "32rem",
          height: viewEditorSize.height ? `${viewEditorSize.height}px` : undefined,
          maxHeight: viewEditorSize.height ? "none" : "85vh",
        }}
      >
        <ResizeHandles onPointerDown={viewEditorPointerDown} />
        <DialogHeader>
          <DialogTitle className="text-[#384872]">
            {view.isDefault ? "Modifier la vue" : "Modifier la vue"}
            {" : "}{view.name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configurer les colonnes et filtres de la vue
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Nom */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700">Nom</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#384872]/30 focus:border-[#384872]"
            />
          </div>

          {/* Colonnes */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">
              Colonnes <span className="text-slate-400 font-normal">(glisser pour réordonner)</span>
            </label>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={displayOrder}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {displayOrder.map((col) => (
                    <SortableColumnItem
                      key={col}
                      col={col}
                      checked={checkedColumns.has(col)}
                      onToggle={toggleColumn}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          {/* Filtres */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">Filtres</label>
            <div className="grid grid-cols-2 gap-2">
              {allColumns
                .filter((col) => col !== "id")
                .map((col) => (
                  <div key={col} className="space-y-0.5">
                    <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                      {col}
                    </label>
                    {renderFilterControl(col, filters[col], setFilter, lookupMaps)}
                  </div>
                ))}
            </div>
          </div>
        </div>

        <DialogFooter className="pt-3 border-t">
          <Button variant="outline" size="sm" onClick={onClose}>
            Annuler
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            className="bg-[#384872] hover:bg-[#2d3a5c] text-white"
          >
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- ViewSelector ---

export function ViewSelector({
  views,
  activeView,
  onSwitch,
  onEdit,
  onCreateNew,
  onDuplicate,
  onDelete,
}: {
  views: ListView[];
  activeView: ListView;
  onSwitch: (viewId: string) => void;
  onEdit: () => void;
  onCreateNew: () => void;
  onDuplicate: (viewId: string) => void;
  onDelete: (viewId: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {/* View dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5 min-w-[120px] justify-between">
            <span className="truncate">Vue : {activeView.name}</span>
            <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[200px]">
          {views.map((v) => (
            <DropdownMenuItem
              key={v.id}
              onClick={() => onSwitch(v.id)}
              className="flex items-center justify-between"
            >
              <span className="truncate">{v.name}</span>
              {v.id === activeView.id && (
                <Check className="h-4 w-4 text-[#384872] flex-shrink-0" />
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onCreateNew} className="text-[#384872]">
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle vue
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onEdit}
        className="h-8 w-8 p-0 text-slate-400 hover:text-[#384872]"
        title="Modifier la vue"
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>

      {/* More actions */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
            title="Actions"
          >
            <span className="text-lg leading-none">···</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => onDuplicate(activeView.id)}>
            <Copy className="h-4 w-4 mr-2" />
            Dupliquer
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onDelete(activeView.id)}
            disabled={activeView.isDefault}
            className={activeView.isDefault ? "opacity-40 cursor-not-allowed" : "text-red-600"}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Supprimer
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
